import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import * as Analytics from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileColumnsVariant from 'components/AccommodationKit/AccommodationPackageTileColumnsVariant'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayLaterPill from 'components/Common/PayLaterPill'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import type { PriceDetailsModalOfferProps } from 'components/Common/PriceDetails/PriceDetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { SOURCE_TYPES } from 'constants/source'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { pluralizeToString } from 'lib/string/pluralize'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import {
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  getPropertyDestinationAirportCode,
  getPropertyOfferMapping,
} from 'selectors/offerPage/offerPageSelectors'
import { getSource } from 'selectors/offerSelectors'

import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'
import { propertySingleRoomBookNowOrAddToCartCallback } from './propertyBuyCallback'

interface Props {
  shouldUseAccomContent: boolean
  property: App.Property.Property
  isPropertyUltraLux: boolean
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  lpcOffer: App.HotelOffer | undefined
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  bedbankOffer: App.BedbankOffer | undefined
  rate: App.PropertyRate
  optionNumber: number // TODO one-indexed
  showOptionLabel: boolean
  originAirport: App.Airport | undefined
  destinationAirportCode: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isLoggedIn: boolean
  isSpoofed: boolean
  subscriber: App.OrderSubscriber | undefined
  canShowMargin: boolean
  // TODO analytics
  roomListId: string
  roomPosition: number // TODO one-indexed
  rateListId: string
}

function PropertyRateOption(props: Props) {
  const {
    shouldUseAccomContent,
    property,
    isPropertyUltraLux,
    lpcOffer,
    bedbankOffer,
    rate,
    optionNumber,
    showOptionLabel,
    originAirport,
    destinationAirportCode,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    source,
    utmContent,
    roomOption,
    isLoggedIn,
    isSpoofed,
    subscriber,
    canShowMargin,
    roomListId,
    roomPosition,
    rateListId,
  } = props

  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const [ref, inView] = useInView({ triggerOnce: true })

  // AB Test: Merged package options
  // Note: Optimizely audience filters desktop users, but we also check viewport size
  // to handle window resizing (desktop user shrinking browser to mobile size)
  const isDesktop = useScreenSizeUp('desktop')
  const isMergedPackageOptionsEnabled = !!useOptimizelyExperiment(OptimizelyExperiments.croNewPackageColumnsTest, isDesktop)

  const [propertyTimeZone, countryCode, isUltraLux] = shouldUseAccomContent ?
      [property.location.timezone, property.location.countryCode, isPropertyUltraLux] :
      // NB: If `spp_use_accom_content` is disabled, then `lpcOffer` is guaranteed to be defined.
      [
        (rate.service.type === 'bedbank' && bedbankOffer) ? bedbankOffer.property.timezone : lpcOffer!.property.timezone,
        lpcOffer!.property.geoData.countryCode,
        !!lpcOffer!.property.isUltraLux,
      ]
  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined
  const packageNumber = showOptionLabel ? optionNumber : undefined
  const rawBedbankDiscount = useBedbankDiscount(rate.totals.discount, rate.luxPlusTotals?.discount ?? 0)
  // TODO(HP-1404): Show margin details for LuxPlus+ prices.
  const shouldShowMargin = !!(canShowMargin && rate.totals.margin && rate.totals.marginAud)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode: countryCode,
        currentRegionCode: regionCode,
        isUltraLux,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
    ]
  }, [rate, countryCode, regionCode, isUltraLux])

  useEffect(() => {
    if (inView && rooms && checkIn && checkOut) {
      Analytics.trackEvent(propertyRateImpressionEventWithContext({
        occupancy: rooms,
        checkIn,
        checkOut,
        roomListId,
        roomPosition,
        rateListId,
        ratePosition: optionNumber,
        rate,
      }))
    }
  }, [
    checkIn,
    checkOut,
    inView,
    optionNumber,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
  ])

  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren\'t set')
    trackEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      offerPageDispatch,
      modalElementContext,
      showModal,
      property,
      rate,
      optionNumber,
      originAirportCode: originAirport?.code,
      destinationAirportCode,
      checkIn,
      checkOut,
      duration,
      rooms,
      regionCode,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      subscriber,
      roomListId,
      roomPosition,
      rateListId,
    })
  }, [
    checkIn,
    checkOut,
    destinationAirportCode,
    dispatch,
    duration,
    isLoggedIn,
    isSpoofed,
    modalElementContext,
    offerPageDispatch,
    optionNumber,
    originAirport?.code,
    property,
    rate,
    rateListId,
    regionCode,
    roomListId,
    roomOption,
    roomPosition,
    rooms,
    showModal,
    source,
    subscriber,
    trackEvent,
    utmContent,
  ])

  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])
  const ctas = <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      includesFlights={rate.includesFlights}
      onCtaClick={onCtaClick}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>

  const cancellationPolicy = <PropertyCancellationPolicy
    propertyTimeZone={propertyTimeZone!}
    headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={rate.fullyRefundableUntil}
    cancellationPolicies={rate.cancellationPolicies}
    currency={rate.currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  /**
   * LPC RATES
   */
  if (rate.service.type === 'lpc') {
    const shouldShowLimitedTimeSpecialLabel = rate.service.isTactical || rate.hasAppliedDiscount
    const lpcDiscount = rate.totals.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.totals.discount : 0
    const shouldShowValue = rate.totals.valuedUpToPrice > rate.totals.sellingPrice
    const showPriceDetails = !!(rate.totals.taxesAndFees || rate.totals.propertyFees)

    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = shouldUseAccomContent ? {
      offerId: rate.service.offerId,
      offerType: OFFER_TYPE_ALWAYS_ON,
      saleUnit: 'room',
      displayPricingAsPerPerson: false,
    } : { offer: lpcOffer! }

    // Call the appropriate render method
    if (!isMergedPackageOptionsEnabled) {
      return (<AccommodationPackageTile data-testid={rate.service.rateId}>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            packageNumber={packageNumber}
            rateName={rate.name}
            tagLineLabel={<>
              {shouldShowLimitedTimeSpecialLabel && <ProductTypeLabel productType="limited_time_special" />}
            </>}
          >
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              <Group direction="vertical" gap={4} noDisplayWhenEmpty>
                {cancellationPolicy}
              </Group>
            </Group>
          </AccommodationPackageTileDetails>
          <div>
            <LargeContentToggle
              maxHeight={208}
              showMoreText="View more inclusions"
              showLessText="View less inclusions"
            >
              <VerticalSpacer gap={12}>
                {rate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
                  luxPlusInclusions={rate.luxPlusInclusions}
                  accommodationOffer={lpcOffer}
                />}
                {rate.bonusInclusions.length > 0 && <OfferPagePackageInclusionsList title="bonus">
                  {rate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </OfferPagePackageInclusionsList>}
                {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
                  {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </VerticalSpacer>}
              </VerticalSpacer>
            </LargeContentToggle>
          </div>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap">
            {lpcDiscount > 0 && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={lpcDiscount}
            />}
          </Group>}
          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock
              title={undefined}
              callToAction={ctas}
            >
              {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
              {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>
                          {rate.includesFlights && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', duration)} {rate.includesFlights && '+ flights from'}
                        </PriceRowPriceCaption>
                        <LuxPlusPriceGuard
                          showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                          fallbackPrice={<PriceRowPrice
                            data-testid="property-package-price"
                            size="M"
                            price={rate.totals.displayPrice}
                            saleUnit="total"
                          />}
                        >
                          <LuxPlusPriceStack
                            size="M"
                            testid="property-package-price"
                            price={rate.totals.displayPrice}
                            memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                            saleUnit="total"
                          />
                        </LuxPlusPriceGuard>
                        <div ref={ref}></div>
                        <PriceRowTaxesAndFees />
                        {shouldShowValue && <PriceRowValueDiscountWithCaption
                          size="M"
                          originalValue={rate.totals.valuedUpToPrice}
                        />}
                      </div>
                      {rate.exceedsIncludedGuests && <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={rate.includedGuestsExceededPolicy}
                      />}
                      {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                      {showPriceDetails && <OfferPriceDetailsRow
                        trigger="price-row"
                        triggerSize="M"
                        offerPage
                        {...priceDetailsModalOfferProps}
                        duration={duration}
                        propertyFees={rate.totals.propertyFees}
                        hotelPrice={rate.totals.sellingPrice}
                        taxesAndFees={rate.totals.taxesAndFees}
                        hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
                        showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                        rooms={rooms}
                        dueAtPropertyMessage={
                        // TODO(HP-1495): Expose `taxAndFeesContent`.
                          lpcOffer?.property.taxesAndFeesContent ?? ''
                        }
                        cta={<Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
                          {ctas}
                        </Group>}
                      />}
                      {shouldShowMargin && <PriceRowMarginText
                        marginValue={rate.totals.marginAud!}
                        margin={rate.totals.margin}
                        type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                      />}
                    </Group>
                  </Caption>
                </div>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                />
              </VerticalSpacer>}
              {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="neutral-two" variant="small">
                  No flights available from {originAirport?.name}. Please select another departing airport.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="neutral-two" variant="small">
                  Flights cannot be booked online when check-in is within 24 hours.
                </BodyText>}
                {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="neutral-two" variant="small">
                  Flights can only be booked up to 11 months in advance.
                </BodyText>}
                {rate.cheapestFlightPrice === 0 && <BodyText colour="neutral-two" variant="small">
                  Add flights to the hotel package. Select from all major airlines at the next step.
                </BodyText>}
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
      ) }

    return (<AccommodationPackageTileColumnsVariant data-testid={rate.service.rateId}>
      <AccommodationPackageTilePricingArea>
        {(!rate.includesFlights ||
          (typeof rate.cheapestFlightPrice === 'number' &&
            rate.cheapestFlightPrice > 0)) && (
          <Group direction="horizontal" gap={4} wrap="wrap">
            {shouldShowLimitedTimeSpecialLabel && <ProductTypeLabel productType="limited_time_special" />}
            {lpcDiscount > 0 && (
              <PriceRowValueDiscount type="estimate" discountPercentage={lpcDiscount} />
            )}
          </Group>
        )}

        <AccommodationPackageTilePricingBlocks>
          <AccommodationPackageTilePricingBlock title={undefined}>
            {rate.includesFlights &&
              rate.cheapestFlightPrice === 'loading' && (
              <LoadingIndicator floating opaque />
            )}

            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>
                          {rate.includesFlights && (
                            <LinePlaneDepartureIcon size="S" />
                          )}{' '}
                          {pluralizeToString('night', duration)}{' '}
                          {rate.includesFlights && '+ flights from'}
                        </PriceRowPriceCaption>

                        <LuxPlusPriceGuard
                          showMemberPrice={
                            (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                          }
                          fallbackPrice={
                            <PriceRowPrice
                              data-testid="property-package-price"
                              size="M"
                              price={rate.totals.displayPrice}
                              saleUnit="total"
                            />
                          }
                        >
                          <LuxPlusPriceStack
                            size="M"
                            testid="property-package-price"
                            price={rate.totals.displayPrice}
                            memberPrice={
                              rate.luxPlusTotals?.displayPrice ?? 0
                            }
                            saleUnit="total"
                          />
                        </LuxPlusPriceGuard>

                        <div ref={ref} />
                        <PriceRowTaxesAndFees />

                        {shouldShowValue && (
                          <PriceRowValueDiscountWithCaption
                            size="M"
                            originalValue={rate.totals.valuedUpToPrice}
                          />
                        )}
                      </div>

                      {rate.exceedsIncludedGuests && (
                        <AdditionalGuestsPopup
                          complex={false}
                          description="+ Extra guest surcharge"
                          modalContent={rate.includedGuestsExceededPolicy}
                        />
                      )}

                      {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                      {showPriceDetails && <OfferPriceDetailsRow
                        trigger="price-row"
                        triggerSize="M"
                        offerPage
                        {...priceDetailsModalOfferProps}
                        duration={duration}
                        propertyFees={rate.totals.propertyFees}
                        hotelPrice={rate.totals.sellingPrice}
                        taxesAndFees={rate.totals.taxesAndFees}
                        hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
                        showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                        rooms={rooms}
                        dueAtPropertyMessage={
                          // TODO(HP-1495): Expose `taxAndFeesContent`.
                          lpcOffer?.property.taxesAndFeesContent ?? ''
                        }
                        cta={<Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
                          {ctas}
                        </Group>}
                      />}

                      {shouldShowMargin && (
                        <PriceRowMarginText
                          marginValue={rate.totals.marginAud!}
                          margin={rate.totals.margin}
                          type={
                            rate.includesFlights
                              ? 'property_estimate_accom'
                              : 'property_estimate'
                          }
                        />
                      )}
                    </Group>
                  </Caption>
                </div>

                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={
                    rate.includesFlights ? 'estimate' : 'exact'
                  }
                />
              </VerticalSpacer>
            )}

            {rate.includesFlights &&
              (typeof rate.cheapestFlightPrice === 'string' ||
                rate.cheapestFlightPrice === 0) && (
              <VerticalSpacer gap={12}>
                {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                  rate.cheapestFlightPrice === 'unavailable') && (
                  <BodyText colour="neutral-two" variant="small">
                    No flights available from {originAirport?.name}. Please
                    select another departing airport.
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooEarly' && (
                  <BodyText colour="neutral-two" variant="small">
                    Flights cannot be booked online when check-in is
                    within 24 hours.
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 'departureTooLate' && (
                  <BodyText colour="neutral-two" variant="small">
                    Flights can only be booked up to 11 months in advance.
                  </BodyText>
                )}

                {rate.cheapestFlightPrice === 0 && (
                  <BodyText colour="neutral-two" variant="small">
                    Add flights to the hotel package. Select from all
                    major airlines at the next step.
                  </BodyText>
                )}
              </VerticalSpacer>
            )}
          </AccommodationPackageTilePricingBlock>
        </AccommodationPackageTilePricingBlocks>
      </AccommodationPackageTilePricingArea>

      <Group direction="vertical" gap={8} style={{ gridArea: 'cta' }}>
        {ctas}
      </Group>
    </AccommodationPackageTileColumnsVariant>)
  }

  /**
   * BEDBANK RATES
   */
  if (rate.service.type === 'bedbank' && (bedbankOffer || shouldUseAccomContent)) {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0

    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = shouldUseAccomContent ? {
      offerId: rate.service.propertyId,
      offerType: OFFER_TYPE_BED_BANK,
      saleUnit: 'room',
      // TODO(HP-1614): Handle `bedbankOffer.displayPricingAsPerPerson`.
      displayPricingAsPerPerson: false,
    } : { offer: bedbankOffer! }

    if (!isMergedPackageOptionsEnabled) {
      return (
        <AccommodationPackageTile>
          <AccommodationPackageTileContentArea>
            <AccommodationPackageTileDetails packageNumber={packageNumber}>
              <Group direction="vertical" gap={8} noDisplayWhenEmpty>
                {cancellationPolicy}
              </Group>
            </AccommodationPackageTileDetails>
            <div>
              {/* TODO !!rate.promotions.length && <div></div> */}
              {rate.inclusions.length > 0 && (
                <VerticalSpacer gap={4}>
                  {rate.inclusions.map(inclusion => (
                    <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />
                  ))}
                </VerticalSpacer>
              )}
            </div>
          </AccommodationPackageTileContentArea>

          <AccommodationPackageTilePricingArea>
            {(!rate.includesFlights ||
              (typeof rate.cheapestFlightPrice === 'number' &&
                rate.cheapestFlightPrice > 0)) && (
              <Group direction="horizontal" gap={4}>
                {bedbankDiscount > 0 && (
                  <PriceRowValueDiscount
                    type="estimate"
                    discountPercentage={bedbankDiscount}
                  />
                )}
                {rate.hasPaymentSchedules && <PayLaterPill />}
              </Group>
            )}

            <AccommodationPackageTilePricingBlocks>
              <AccommodationPackageTilePricingBlock
                title={undefined}
                callToAction={ctas}
              >
                {rate.includesFlights &&
                  rate.cheapestFlightPrice === 'loading' && (
                  <LoadingIndicator floating opaque />
                )}

                {(!rate.includesFlights ||
                  (typeof rate.cheapestFlightPrice === 'number' &&
                    rate.cheapestFlightPrice > 0)) && (
                  <VerticalSpacer gap={12}>
                    <div>
                      <PriceRowPriceCaption>
                        {rate.includesFlights && (
                          <LinePlaneDepartureIcon size="S" />
                        )}{' '}
                        {pluralizeToString('night', duration)}
                        {rate.includesFlights && ' + flights'} from
                      </PriceRowPriceCaption>

                      <LuxPlusPriceGuard
                        showMemberPrice={
                          (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                        }
                        fallbackPrice={
                          <PriceRowPrice
                            data-testid="property-package-price"
                            size="M"
                            price={rate.totals.displayPrice}
                            saleUnit="total"
                          />
                        }
                      >
                        <LuxPlusPriceStack
                          size="M"
                          testid="property-package-price"
                          price={rate.totals.displayPrice}
                          memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                          saleUnit="total"
                        />
                      </LuxPlusPriceGuard>

                      <div ref={ref} />
                      <PriceRowTaxesAndFees />

                      {!!rawBedbankDiscount && (
                        <PriceRowValueDiscountWithCaption
                          size="M"
                          originalValue={rate.totals.valuedUpToPrice}
                        />
                      )}

                      {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                      <OfferPriceDetailsRow
                        trigger="price-row"
                        triggerSize="M"
                        offerPage
                        {...priceDetailsModalOfferProps}
                        rooms={rooms}
                        propertyFees={rate.totals.propertyFees}
                        taxesAndFees={rate.totals.taxesAndFees}
                        hotelPrice={rate.totals.sellingPrice}
                        hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
                        showMemberPrice={
                          (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                        }
                        duration={duration}
                        cta={
                          <Group
                            direction="vertical"
                            tabletDirection="horizontal-reverse"
                            tabletHorizontalAlign="end"
                            gap={8}
                          >
                            {ctas}
                          </Group>
                        }
                      />

                      {shouldShowMargin && (
                        <PriceRowMarginText
                          marginValue={rate.totals.marginAud!}
                          margin={rate.totals.margin}
                          supplier={rate.service.supplier}
                          type={
                            rate.includesFlights
                              ? 'property_estimate_accom'
                              : 'property_estimate'
                          }
                        />
                      )}
                    </div>

                    <LoyaltyPointsEarnWithBreakdownModal
                      calculationRequests={pointsEarnCalculationRequests}
                      calculationType={
                        rate.includesFlights ? 'estimate' : 'exact'
                      }
                    />
                  </VerticalSpacer>
                )}

                {rate.includesFlights &&
                  (typeof rate.cheapestFlightPrice === 'string' ||
                    rate.cheapestFlightPrice === 0) && (
                  <VerticalSpacer gap={12}>
                    {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                      rate.cheapestFlightPrice === 'unavailable') && (
                      <BodyText colour="neutral-two" variant="small">
                        No flights available from {originAirport?.name}. Please
                        select another departing airport.
                      </BodyText>
                    )}

                    {rate.cheapestFlightPrice === 'departureTooEarly' && (
                      <BodyText colour="neutral-two" variant="small">
                        Flights cannot be booked online when check-in is
                        within 24 hours.
                      </BodyText>
                    )}

                    {rate.cheapestFlightPrice === 'departureTooLate' && (
                      <BodyText colour="neutral-two" variant="small">
                        Flights can only be booked up to 11 months in advance.
                      </BodyText>
                    )}

                    {rate.cheapestFlightPrice === 0 && (
                      <BodyText colour="neutral-two" variant="small">
                        Add flights to the hotel package. Select from all
                        major airlines at the next step.
                      </BodyText>
                    )}
                  </VerticalSpacer>
                )}
              </AccommodationPackageTilePricingBlock>
            </AccommodationPackageTilePricingBlocks>
          </AccommodationPackageTilePricingArea>
        </AccommodationPackageTile>
      )
    }

    return (
      <AccommodationPackageTileColumnsVariant>
        <AccommodationPackageTilePricingArea>
          {(!rate.includesFlights ||
            (typeof rate.cheapestFlightPrice === 'number' &&
              rate.cheapestFlightPrice > 0)) && (
            <Group direction="horizontal" gap={4}>
              {bedbankDiscount > 0 && (
                <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={bedbankDiscount}
                />
              )}
              {rate.hasPaymentSchedules && <PayLaterPill />}
            </Group>
          )}

          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock title={undefined}>
              {rate.includesFlights &&
                rate.cheapestFlightPrice === 'loading' && (
                <LoadingIndicator floating opaque />
              )}

              {(!rate.includesFlights ||
                (typeof rate.cheapestFlightPrice === 'number' &&
                  rate.cheapestFlightPrice > 0)) && (
                <VerticalSpacer gap={12}>
                  <div>
                    <PriceRowPriceCaption>
                      {rate.includesFlights && (
                        <LinePlaneDepartureIcon size="S" />
                      )}{' '}
                      {pluralizeToString('night', duration)}
                      {rate.includesFlights && ' + flights'} from
                    </PriceRowPriceCaption>

                    <LuxPlusPriceGuard
                      showMemberPrice={
                        (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                      }
                      fallbackPrice={
                        <PriceRowPrice
                          data-testid="property-package-price"
                          size="M"
                          price={rate.totals.displayPrice}
                          saleUnit="total"
                        />
                      }
                    >
                      <LuxPlusPriceStack
                        size="M"
                        testid="property-package-price"
                        price={rate.totals.displayPrice}
                        memberPrice={rate.luxPlusTotals?.displayPrice ?? 0}
                        saleUnit="total"
                      />
                    </LuxPlusPriceGuard>

                    <div ref={ref} />
                    <PriceRowTaxesAndFees />

                    {!!rawBedbankDiscount && (
                      <PriceRowValueDiscountWithCaption
                        size="M"
                        originalValue={rate.totals.valuedUpToPrice}
                      />
                    )}

                    {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                    <OfferPriceDetailsRow
                      trigger="price-row"
                      triggerSize="M"
                      offerPage
                      {...priceDetailsModalOfferProps}
                      rooms={rooms}
                      propertyFees={rate.totals.propertyFees}
                      taxesAndFees={rate.totals.taxesAndFees}
                      hotelPrice={rate.totals.sellingPrice}
                      hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
                      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                      duration={duration}
                      cta={<Group direction="vertical" tabletDirection="horizontal-reverse" tabletHorizontalAlign="end" gap={8}>
                        {ctas}
                      </Group>}
                    />
                    {shouldShowMargin && <PriceRowMarginText
                      marginValue={rate.totals.marginAud!}
                      margin={rate.totals.margin}
                      supplier={rate.service.supplier}
                      type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                    />}
                  </div>
                  <LoyaltyPointsEarnWithBreakdownModal
                    calculationRequests={pointsEarnCalculationRequests}
                    calculationType={rate.includesFlights ? 'estimate' : 'exact'}
                  />
                </VerticalSpacer>)}
              {rate.includesFlights &&
                (typeof rate.cheapestFlightPrice === 'string' ||
                  rate.cheapestFlightPrice === 0) && (
                <VerticalSpacer gap={12}>
                  {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
                    rate.cheapestFlightPrice === 'unavailable') && (
                    <BodyText colour="neutral-two" variant="small">
                      No flights available from {originAirport?.name}. Please select another departing airport.
                    </BodyText>
                  )}
                  {rate.cheapestFlightPrice === 'departureTooEarly' && (
                    <BodyText colour="neutral-two" variant="small">
                      Flights cannot be booked online when check-in is within 24 hours.
                    </BodyText>
                  )}
                  {rate.cheapestFlightPrice === 'departureTooLate' && (
                    <BodyText colour="neutral-two" variant="small">
                      Flights can only be booked up to 11 months in advance.
                    </BodyText>
                  )}
                  {rate.cheapestFlightPrice === 0 && (
                    <BodyText colour="neutral-two" variant="small">
                      Add flights to the hotel package. Select from all major airlines at the next step.
                    </BodyText>
                  )}
                </VerticalSpacer>
              )}
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>

        <Group direction="vertical" gap={8} style={{ gridArea: 'cta' }}>
          {ctas}
        </Group>
      </AccommodationPackageTileColumnsVariant>
    )
  }

  return null
}

export default withOfferPageAndReduxState((state, appState) => ({
  shouldUseAccomContent: appState.system.features.flags[OptimizelyFeatureFlags.sppUseAccomContent],
  property: state.property!,
  isPropertyUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
  originAirport: state.airport,
  destinationAirportCode: getPropertyDestinationAirportCode(state, appState),
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  regionCode: appState.geo.currentRegionCode,
  source: getSource(appState),
  utmContent: appState.router.location.query.utm_content,
  roomOption: state.roomOption,
  isLoggedIn: selectLoggedIn(appState),
  isSpoofed: appState.auth.account.isSpoofed,
  subscriber: getEnquirySubscriber(appState),
  canShowMargin: appState.auth.account.isSpoofed && appState.system.showMarginInSpoofMode,
  open: !!state.activeFilter,
}), PropertyRateOption, preventRenderWithOpenFilter)
